import React, { useMemo, useCallback } from 'react'
import pDebounce from 'p-debounce'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'
import _memoize from 'lodash/memoize'
import { useTranslation } from 'react-i18next'
import Popper from '@mui/material/Popper'
import MuiAutoComplete from 'ggx-componentlibrary/components/autocomplete/mui-autocomplete/mui-autocomplete.component'

import { placesService } from 'ggx-service/api/places.service'
import { apiService as deliveryApiService } from 'ggx-src/delivery/api/api.service'
import {
  buildAddressBookOptions,
  buildSearchedAddressOptions,
  useDefaultOptions,
  FIELD_NAME,
} from './utils'

const fetchPlacesDetails = _memoize(placeId =>
  placesService.placesDetails(placeId)
)

const verifyAddress = _memoize(
  ({ latitude, longitude }) =>
    deliveryApiService.verifyAddress({ latitude, longitude }),
  ({ latitude, longitude }) => `${latitude},${longitude}`
)

function AddressAutocompleteV2({
  address = {},
  latitude = {},
  longitude = {},
  placeholder,
  dataTestid,
  handleResetAddressSyncErrors,
  section,
  onChange,
  onOpenAddressBookModal,
  onInputChange,
  onBlur,
  onHover,
  isAddressBookOptionsReady,
}) {
  const [t] = useTranslation()

  const handleAutocompleteQuery = useMemo(() => {
    const debouncedPlacesAutoComplete = pDebounce(
      value =>
        Promise.all([
          buildAddressBookOptions(t)(FIELD_NAME.ADDRESS, value),
          buildSearchedAddressOptions(value),
        ]).then(results => results.flat()),
      500
    )
    return async newValue => await debouncedPlacesAutoComplete(newValue)
  }, [t])

  const handleAutocompleteChange = useCallback(
    async (valueObj = {}) => {
      if (!address?.input || !latitude?.input || !longitude?.input) return

      handleResetAddressSyncErrors && handleResetAddressSyncErrors(section)
      if (isEmpty(valueObj))
        return onChange({ address: null, latitude: null, longitude: null })

      if (valueObj.groupType === 'location') {
        const {
          description: selectedAddress,
          place_id: placeId,
        } = valueObj.value
        try {
          const detail = await fetchPlacesDetails(placeId)
          const { lat: latitude, lng: longitude } =
            detail.geometry?.location ?? {}
          if (latitude && longitude) {
            onChange({
              address: selectedAddress,
              latitude: latitude.toString(),
              longitude: longitude.toString(),
            })
            await verifyAddress({ latitude, longitude })
          }
        } catch (error) {
          const { message: errorKey } = error?.response?.data?.error || {}
          if (errorKey)
            onChange({
              address: selectedAddress,
              latitude: errorKey,
              longitude: errorKey,
            })
        }
      } else if (valueObj.groupType === 'recent-address') {
        onChange({
          address: valueObj.address,
          latitude: valueObj.latitude.toString(),
          longitude: valueObj.longitude.toString(),
          details: valueObj.detail_address,
          phone_number: valueObj.contact_phone_number,
          name: valueObj.contact_name,
        })
      } else if (valueObj.groupType === 'saved-address-book') {
        onChange({
          address: valueObj.value.address,
          latitude: valueObj.value.latitude.toString(),
          longitude: valueObj.value.longitude.toString(),
          details: valueObj.value.detail_address,
          phone_number: valueObj.value.contact_phone_number,
          name: valueObj.value.contact_name,
          saved_address_id: valueObj.value.saved_address_id,
        })
      }
    },
    [address, latitude, longitude, handleResetAddressSyncErrors, section]
  )

  const value = {
    value: address.input && address.input.value,
    address: address.input && address.input.value,
    latitude: latitude.input && latitude.input.value,
    longitude: longitude.input && longitude.input.value,
  }

  const meta = {
    touched:
      (address.meta && (address.meta.touched || address.meta.dirty)) ||
      (latitude.meta && (latitude.meta.touched || latitude.meta.dirty)) ||
      (longitude.meta && (longitude.meta.touched || longitude.meta.dirty)),
    error:
      (address.meta && address.meta.error) ||
      (latitude.meta && latitude.meta.error) ||
      (longitude.meta && longitude.meta.error),
  }

  const defaultOptions = useDefaultOptions(
    FIELD_NAME.ADDRESS,
    address.input?.value ?? '',
    isAddressBookOptionsReady
  )

  return (
    <MuiAutoComplete
      selectedOption={value}
      onQuery={handleAutocompleteQuery}
      onChange={handleAutocompleteChange}
      onInputChange={onInputChange}
      onBlur={({ address: searchText }) => {
        onBlur()
        address.input.onBlur(searchText)
      }}
      onHover={onHover}
      defaultOptions={defaultOptions}
      placeholder={placeholder}
      supportEditSelectedValue
      meta={meta}
      showSaveAddressModal={onOpenAddressBookModal}
      dataTestid={dataTestid}
      customPopperComponent={({ style, ...props }) => (
        <Popper
          style={{ width: '453px' }}
          {...props}
          placement="bottom-start"
        />
      )}
    />
  )
}

const reduxFormPropTypeShape = PropTypes.shape({
  input: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }),
})

AddressAutocompleteV2.propTypes = {
  address: reduxFormPropTypeShape,
  latitude: reduxFormPropTypeShape,
  longitude: reduxFormPropTypeShape,
  placeholder: PropTypes.string,
  dataTestid: PropTypes.string,
  handleResetAddressSyncErrors: PropTypes.func,
  section: PropTypes.string,
  onChange: PropTypes.func,
  onOpenAddressBookModal: PropTypes.func,
  onInputChange: PropTypes.func,
  onBlur: PropTypes.func,
  onHover: PropTypes.func,
  isAddressBookOptionsReady: PropTypes.bool,
}

export { AddressAutocompleteV2 }
